import React from 'react'
import PropTypes from 'prop-types'
import get from 'lodash.get'
import { useFormattingContext } from '../../../FormattingProvider/FormattingContext'

function DetailHeader ({ name, onClose }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '16px 20px',
        borderBottom: '1px solid var(--color-neutral-200)'
      }}
    >
      <span style={{ fontWeight: 600, fontSize: '0.9375rem', color: 'var(--root-color)' }}>
        {name || 'Details'}
      </span>
      <button
        onClick={onClose}
        aria-label='Close details panel'
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          fontSize: '1.25rem',
          color: 'var(--color-neutral-500)',
          padding: '4px',
          lineHeight: 1
        }}
      >
        &times;
      </button>
    </div>
  )
}

DetailHeader.propTypes = {
  name: PropTypes.string,
  onClose: PropTypes.func.isRequired
}

function DetailField ({ label, value }) {
  if (value == null || value === '') return null
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        padding: '8px 0',
        fontSize: '0.8125rem',
        borderBottom: '1px solid var(--color-neutral-100)'
      }}
    >
      <span style={{ color: 'var(--color-neutral-500)', flexShrink: 0, marginRight: 12 }}>{label}</span>
      <span style={{ color: 'var(--root-color)', fontWeight: 500, textAlign: 'right', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {String(value)}
      </span>
    </div>
  )
}

DetailField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
}

function DetailFieldList ({ fields, raw, formatter }) {
  if (!Array.isArray(fields) || !fields.length) {
    return <RawFieldList raw={raw} />
  }

  return (
    <div>
      {fields.map(field => {
        const rawValue = get(raw, field.accessor)
        if (rawValue == null) return null

        const displayValue = field.format && formatter
          ? formatter(rawValue, field.format)
          : rawValue

        return (
          <DetailField
            key={field.accessor}
            label={field.header || field.accessor}
            value={displayValue}
          />
        )
      })}
    </div>
  )
}

DetailFieldList.propTypes = {
  fields: PropTypes.arrayOf(PropTypes.shape({
    accessor: PropTypes.string.isRequired,
    header: PropTypes.string,
    format: PropTypes.string
  })),
  raw: PropTypes.object,
  formatter: PropTypes.func
}

function RawFieldList ({ raw }) {
  if (!raw) return null

  return (
    <div>
      <div style={{ fontWeight: 600, fontSize: '0.75rem', color: 'var(--color-neutral-500)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        All Fields
      </div>
      {Object.entries(raw).map(([key, val]) => {
        if (val == null || typeof val === 'object') return null
        return <DetailField key={key} label={key} value={val} />
      })}
    </div>
  )
}

RawFieldList.propTypes = {
  raw: PropTypes.object
}

function FlowGraphDetailPanel ({ node, onClose, levels, open }) {
  const { formatter } = useFormattingContext()

  const { nameValue, raw, levelId } = node?.data || {}

  const levelConfig = Array.isArray(levels)
    ? levels.find(l => l.id === levelId)
    : null

  const detailFields = levelConfig?.detailFields

  return (
    <div
      style={{
        position: 'absolute',
        top: 0,
        right: 0,
        bottom: 0,
        width: 320,
        background: 'var(--color-surface-050)',
        borderLeft: '1px solid var(--color-neutral-200)',
        boxShadow: '-4px 0 12px rgba(0, 0, 0, 0.08)',
        zIndex: 10,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        transform: open ? 'translateX(0)' : 'translateX(100%)',
        transition: 'transform 0.25s ease-in-out'
      }}
    >
      <DetailHeader name={nameValue} onClose={onClose} />
      <div style={{ padding: '20px', overflowY: 'auto', flex: 1 }}>
        {node && (
          <DetailFieldList
            fields={detailFields}
            raw={raw}
            formatter={formatter}
          />
        )}
      </div>
    </div>
  )
}

FlowGraphDetailPanel.propTypes = {
  node: PropTypes.shape({
    id: PropTypes.string,
    data: PropTypes.object
  }),
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  levels: PropTypes.array
}

export default FlowGraphDetailPanel
